import React from 'react';
import { Link } from 'react-router-dom';

/**
 * ReportCardSection - Showcases Report Card & Performance Analysis
 * 
 * AI IMAGE PROMPT for report-card-feature.jpg:
 * "Modern performance analytics dashboard showing detailed student report card with 
 * colorful charts, graphs, and progress indicators. Student reviewing comprehensive 
 * performance metrics on a tablet with floating 3D data visualizations (pie charts, 
 * bar graphs, trend lines). Clean purple and green gradient background, 
 * professional UI design, soft ambient lighting, data-driven education theme"
 */

const ReportCardSection: React.FC = () => {
  const insights = [
    {
      icon: 'fas fa-chart-pie',
      title: 'Subject-wise Analysis',
      description: 'Detailed breakdown of performance in each subject',
      color: '#9b59b6'
    },
    {
      icon: 'fas fa-brain',
      title: 'Strength & Weakness',
      description: 'AI identifies your strong topics and improvement areas',
      color: '#e74c3c'
    },
    {
      icon: 'fas fa-trending-up',
      title: 'Progress Tracking',
      description: 'Monitor your improvement over time with trend graphs',
      color: '#27ae60'
    },
    {
      icon: 'fas fa-users',
      title: 'Peer Comparison',
      description: 'See how you rank compared to other students',
      color: '#3498db'
    }
  ];

  const metrics = [
    {
      label: 'Overall Score',
      value: '85%',
      icon: 'fas fa-star',
      color: '#f39c12'
    },
    {
      label: 'National Rank',
      value: '#142',
      icon: 'fas fa-trophy',
      color: '#e74c3c'
    },
    {
      label: 'Tests Taken',
      value: '47',
      icon: 'fas fa-clipboard-check',
      color: '#3498db'
    }
  ];

  return (
    <section className="report-card-section py-5" style={{ background: '#ffffff' }}>
      <div className="container">
        <div className="row align-items-center">
          {/* Left - Content */}
          <div className="col-lg-6 mb-4 mb-lg-0 order-2 order-lg-1" data-aos="fade-right">
            <div className="pe-lg-4">
              {/* Badge */}
              <span 
                className="badge rounded-pill px-3 py-2 mb-3"
                style={{ background: '#9b59b620', color: '#9b59b6', fontSize: '14px', fontWeight: 600 }}
              >
                <i className="fas fa-file-alt me-2"></i>Performance Analytics
              </span>

              {/* Heading */}
              <h2 className="fw-bold mb-3" style={{ fontSize: '2.5rem', lineHeight: '1.2' }}>
                Get <span style={{ color: '#9b59b6' }}>Detailed Insights</span> with AI Report Cards
              </h2>

              {/* Description */}
              <p className="text-muted mb-4" style={{ fontSize: '1.1rem' }}>
                Receive comprehensive performance reports after every test. WiZiAi analyzes 
                your performance, identifies patterns, and provides personalized recommendations 
                to help you improve faster and achieve your target score.
              </p>

              {/* Metrics Row */}
              <div className="row g-3 mb-4">
                {metrics.map((metric, index) => (
                  <div key={index} className="col-4">
                    <div 
                      className="text-center p-3 rounded-4"
                      style={{ background: metric.color + '15', border: `2px solid ${metric.color}30` }}
                    >
                      <i className={`${metric.icon} fa-2x mb-2`} style={{ color: metric.color }}></i>
                      <div className="h4 fw-bold mb-0" style={{ color: metric.color }}>
                        {metric.value}
                      </div>
                      <small className="text-muted">{metric.label}</small>
                    </div>
                  </div>
                ))}
              </div>

              {/* Insights Grid */}
              <div className="row g-3 mb-4">
                {insights.map((insight, index) => (
                  <div key={index} className="col-6">
                    <div className="d-flex align-items-start">
                      <div 
                        className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{ width: '40px', height: '40px', background: insight.color + '20', color: insight.color }}
                      >
                        <i className={insight.icon}></i>
                      </div>
                      <div>
                        <h6 className="fw-bold mb-1 small">{insight.title}</h6>
                        <small className="text-muted" style={{ fontSize: '0.85rem' }}>
                          {insight.description}
                        </small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* AI Recommendations Box */}
              <div 
                className="p-4 rounded-4 mb-4"
                style={{ 
                  background: 'linear-gradient(135deg, #9b59b615, #8e44ad15)',
                  border: '2px solid #9b59b630'
                }}
              >
                <div className="d-flex align-items-start">
                  <div 
                    className="rounded-circle d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                    style={{ width: '50px', height: '50px', background: '#9b59b620', color: '#9b59b6' }}
                  >
                    <i className="fas fa-robot fa-lg"></i>
                  </div>
                  <div>
                    <h6 className="fw-bold mb-2">AI-Powered Recommendations</h6>
                    <p className="mb-0 small text-muted">
                      Get personalized study suggestions, topic-wise improvement plans, 
                      and time management strategies based on your performance data.
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="d-flex gap-3">
                <Link 
                  to="/report-card" 
                  className="btn btn-lg rounded-pill px-4"
                  style={{ background: 'linear-gradient(135deg, #9b59b6, #8e44ad)', color: 'white', border: 'none' }}
                >
                  <i className="fas fa-chart-line me-2"></i>
                  View My Report
                </Link>
                <Link 
                  to="/performance" 
                  className="btn btn-lg btn-outline-secondary rounded-pill px-4"
                >
                  Performance
                  <i className="fas fa-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Right - Image */}
          <div className="col-lg-6 mb-4 mb-lg-0 order-1 order-lg-2" data-aos="fade-left">
            <div className="position-relative">
              <img 
                src="/img/report-card-feature.jpg" 
                alt="Performance Report Card" 
                className="img-fluid rounded-4 shadow-lg"
                style={{ width: '100%', height: 'auto' }}
              />
              {/* Floating Achievement Badge */}
              <div 
                className="position-absolute top-0 end-0 m-4 p-3 rounded-4 shadow-lg"
                style={{ background: 'white' }}
              >
                <div className="text-center">
                  <i className="fas fa-award fa-3x mb-2" style={{ color: '#f39c12' }}></i>
                  <div className="fw-bold">Top 5%</div>
                  <small className="text-muted">This Month</small>
                </div>
              </div>

              {/* Floating Progress Badge */}
              <div 
                className="position-absolute bottom-0 start-0 m-4 px-4 py-3 rounded-4 shadow-lg"
                style={{ background: 'linear-gradient(135deg, #27ae60, #229954)', color: 'white' }}
              >
                <div className="d-flex align-items-center">
                  <i className="fas fa-arrow-up fa-2x me-3"></i>
                  <div>
                    <div className="fw-bold">+15% Improvement</div>
                    <small>Last 30 days</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReportCardSection;
